import { Component, Input, OnChanges } from '@angular/core';
import Konva from 'konva';
import { Group } from 'konva/lib/Group';
import { interval, Subscription } from 'rxjs';
import { ILandmarkFound } from 'src/modules/shared/models/interfaces/landmarks.interface';
import { IPageDimensions } from 'src/modules/shared/models/interfaces/page-dimensions.interface';
import { ISmartCanvasLayer, ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { RobotPreview, RobotWordClick } from 'src/modules/shared/models/robots.model';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';

@Component({
  selector: 'hcr-smart-canvas-landmarks',
  template: '',
})
export class SmartCanvasLandmarksComponent implements OnChanges {
  @Input() selectedRobotFilePreview: RobotPreview;
  @Input() layer: ISmartCanvasLayer;
  @Input() konvaStage: any;
  @Input() stagePageSizes: IPageDimensions[];
  @Input() smartCanvasOptions: ISmartCanvasOptions;

  private _stageLoaded = false;
  private _stageLoopControl: Subscription;
  private _groups: { [key: string]: { data: ILandmarkFound[]; konvaGroup: any } } = {};
  private _landmarkIcon =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzODQgNTEyIj48IS0tISBGb250IEF3ZXNvbWUgUHJvIDYuMi4wIGJ5IEBmb250YXdlc29tZSAtIGh0dHBzOi8vZm9udGF3ZXNvbWUuY29tIExpY2Vuc2UgLSBodHRwczovL2ZvbnRhd2Vzb21lLmNvbS9saWNlbnNlIChDb21tZXJjaWFsIExpY2Vuc2UpIENvcHlyaWdodCAyMDIyIEZvbnRpY29ucywgSW5jLiAtLT48cGF0aCBkPSJNMjE1LjcgNDk5LjJDMjY3IDQzNSAzODQgMjc5LjQgMzg0IDE5MkMzODQgODYgMjk4IDAgMTkyIDBTMCA4NiAwIDE5MmMwIDg3LjQgMTE3IDI0MyAxNjguMyAzMDcuMmMxMi4zIDE1LjMgMzUuMSAxNS4zIDQ3LjQgMHpNMTkyIDI1NmMtMzUuMyAwLTY0LTI4LjctNjQtNjRzMjguNy02NCA2NC02NHM2NCAyOC43IDY0IDY0cy0yOC43IDY0LTY0IDY0eiIvPjwvc3ZnPg==';

  constructor(private _eventEmitterService: EventEmitterService) {
    this._eventEmitterService.get<{ data: RobotWordClick; selectedDocumentIndex: number }>('fortify-canvas-highlight').subscribe((data) => {
      if (data.data) {
        this._fortifyLandmarks(data.data);
      }
    });

    this._eventEmitterService.get('weaken-canvas-highlight').subscribe(() => {
      this._weakenLandmarks();
    });
  }

  private _drawLandmarks(): void {
    this.layer.konvaLayer.destroyChildren();
    this._groups = {};

    this.selectedRobotFilePreview.pages.forEach((page, pageIndex) => {
      page.wordClicks.forEach((wordClick) => {
        if (wordClick.landmarkWordsPositionFound.length > 0 && this._stageLoaded) {
          wordClick.landmarkWordsPositionFound.forEach((landmark) => {
            let group = this._groups[wordClick.key];

            if (!group) {
              group = this._groups[wordClick.key] = {
                data: [],
                konvaGroup: new Konva.Group(),
              };

              this.layer.konvaLayer.add(group.konvaGroup);
            }

            const label = new Konva.Label({
              x: landmark.polygon.centroid.x * this.stagePageSizes[pageIndex].width,
              y: landmark.polygon.coordinates[0].y * this.stagePageSizes[pageIndex].height - 12,
            });

            Konva.Image.fromURL(this._landmarkIcon, (darthNode: any /*Konva.Shape*/) => {
              darthNode.setAttrs({
                x: -6,
                y: -10,
                width: 15,
                height: 20,
                opacity: 0.8,
              });

              label.add(darthNode as Group);
            });

            group.konvaGroup.add(label);
            // this.layer.konvaLayer.add(label);
            group.data.push(landmark);
          });
        }
      });
    });
  }

  private _fortifyLandmarks(data: RobotWordClick): void {
    const highlightKey = data.word?.key;

    Object.keys(this._groups)
      .filter((key) => key !== highlightKey)
      .forEach((key) => {
        this._groups[key]?.konvaGroup.getChildren().forEach((label) => {
          label.opacity(0);
        });
      });
  }

  private _weakenLandmarks(): void {
    Object.keys(this._groups).forEach((key) => {
      this._groups[key]?.konvaGroup.getChildren().forEach((label) => {
        label.opacity(1);
      });
    });
  }

  ngOnChanges(): void {
    if (this.stagePageSizes && this.stagePageSizes.length > 0) {
      this._stageLoaded = true;
    } else {
      this._stageLoaded = false;
    }

    if (this.selectedRobotFilePreview && this.konvaStage) {
      if (this._stageLoopControl) {
        this._stageLoopControl.unsubscribe();
      }

      this._stageLoopControl = interval(100).subscribe(() => {
        if (this._stageLoaded) {
          this._stageLoopControl.unsubscribe();
          this._drawLandmarks();
        }
      });
    }
  }
}
